import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

// Regex: starts with letter or underscore, followed by letters, numbers, or underscores
const VALID_NAME_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;

const isValidVariableName = (name) => {
  if (!name) return true; // Empty is handled separately
  return VALID_NAME_PATTERN.test(name);
};

// Custom Dropdown Component
const TypeDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const options = ['Single Value', 'Array List'];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  TypeDropdown.propTypes = {
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired
  };

  return (
    <div className="custom-dropdown" ref={dropdownRef}>
      <button
        type="button"
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="dropdown-value">{value}</span>
        <svg className="dropdown-chevron" width="10" height="6" viewBox="0 0 10 6">
          <path d="M1 1L5 5L9 1" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>
      {isOpen && (
        <div className="dropdown-menu">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={`dropdown-option ${value === option ? 'selected' : ''}`}
              onClick={() => {
                onChange(option);
                setIsOpen(false);
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const StepVariables = ({ formData, updateFormData, errors }) => {
  const addVariable = () => {
    updateFormData('input_variables', [
      ...formData.input_variables,
      { name: '', description: '', type: 'Single Value' }
    ]);
  };

  const removeVariable = (index) => {
    const updated = formData.input_variables.filter((_, i) => i !== index);
    updateFormData('input_variables', updated);
  };

  const updateVariable = (index, field, value) => {
    // For name field, sanitize by removing invalid characters as user types
    let sanitizedValue = value;
    if (field === 'name') {
      // Remove spaces and special characters except underscore
      sanitizedValue = value.replace(/[^a-zA-Z0-9_]/g, '');
    }
    
    const updated = formData.input_variables.map((v, i) => {
      if (i === index) return { ...v, [field]: sanitizedValue };
      return v;
    });
    updateFormData('input_variables', updated);
  };

  const getNameError = (name) => {
    if (!name) return null;
    if (/^[0-9]/.test(name)) return 'Cannot start with a number';
    if (!isValidVariableName(name)) return 'Only letters, numbers, and _ allowed';
    return null;
  };

  return (
    <div className="wizard-step">
      <div className="step-intro">
        <h3>What inputs will you provide?</h3>
        <p>Define the dynamic variables that will be passed to your prompt.</p>
      </div>

      {errors?.variables && (
        <div className="form-error-message">{errors.variables}</div>
      )}

      {formData.input_variables.length > 0 && (
        <div className="variables-list">
          {formData.input_variables.map((variable, index) => (
            <div key={index} className="variable-card">
              <div className="variable-header">
                <span className="variable-label">Variable {index + 1}</span>
                <button
                  type="button"
                  onClick={() => removeVariable(index)}
                  className="btn-remove-var"
                >
                  Remove
                </button>
              </div>
              <div className="variable-fields">
                <div className="form-group">
                  <label className="form-label-sm">Name</label>
                  <input
                    type="text"
                    value={variable.name}
                    onChange={(e) => updateVariable(index, 'name', e.target.value)}
                    placeholder="e.g., customer_name"
                    className={`form-input ${getNameError(variable.name) ? 'form-error' : ''}`}
                  />
                  {getNameError(variable.name) && (
                    <span className="field-error">{getNameError(variable.name)}</span>
                  )}
                  <span className="field-hint">Only letters, numbers, and underscore (_)</span>
                </div>
                <div className="form-group">
                  <label className="form-label-sm">Description</label>
                  <input
                    type="text"
                    value={variable.description}
                    onChange={(e) => updateVariable(index, 'description', e.target.value)}
                    placeholder="e.g., The customer's full name"
                    className={`form-input ${variable.name && !variable.description ? 'form-error' : ''}`}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label-sm">Type</label>
                  <TypeDropdown
                    value={variable.type || 'Single Value'}
                    onChange={(val) => updateVariable(index, 'type', val)}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="variable-actions">
        <button type="button" onClick={addVariable} className="btn-add-var">
          + Add Variable
        </button>
        {formData.input_variables.length === 0 && (
          <p className="form-hint">
            No variables needed? You can skip this step.
          </p>
        )}
      </div>
    </div>
  );
};

StepVariables.propTypes = {
  formData: PropTypes.shape({
    input_variables: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      description: PropTypes.string,
      type: PropTypes.string,
    })),
  }).isRequired,
  updateFormData: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

StepVariables.defaultProps = {
  errors: {},
};

export default StepVariables;
